import * as React from "react";
import { cn } from "../../utils";
import { BRAND_LOGO_FILES } from "../../brand-logos";

/**
 * Brand marks + catalog metadata for marketing analytics providers.
 *
 * The marks are GENUINE brand logos served from `/public/brand-logos/` via the
 * shared `BRAND_LOGO_FILES` map — the same real assets the Integrations
 * marketplace uses (refresh/extend with `scripts/fetch-brand-logos.sh`) — shown
 * on the canonical white logo chip so every brand color stays legible in both
 * themes. `live` providers have working data fetchers
 * (excellent/src/marketing/analytics.ts); `soon` providers are non-functional
 * roadmap tiles.
 */

export type AnalyticsProviderStatus = "live" | "soon";

export interface AnalyticsProviderMeta {
  id: string;
  name: string;
  description: string;
  status: AnalyticsProviderStatus;
  /** Brand color — used for accents (active states, charts). */
  accent: string;
}

export const ANALYTICS_PROVIDER_CATALOG: AnalyticsProviderMeta[] = [
  {
    id: "plausible",
    name: "Plausible",
    description: "Lightweight, privacy-friendly web analytics. One API key.",
    status: "live",
    accent: "#5850EC",
  },
  {
    id: "ga4",
    name: "Google Analytics 4",
    description: "Google's analytics. Connect with a read-only service account.",
    status: "live",
    accent: "#E8710A",
  },
  {
    id: "fathom",
    name: "Fathom",
    description: "Simple, privacy-first analytics. One API key + site id.",
    status: "live",
    accent: "#7C3AED",
  },
  {
    id: "umami",
    name: "Umami",
    description: "Open-source, self-hostable analytics. Host + website id + key.",
    status: "live",
    accent: "#0EA5E9",
  },
  {
    id: "vercel",
    name: "Vercel Analytics",
    description: "Web vitals + audience for Vercel sites. No public read API yet — not connectable.",
    status: "soon",
    accent: "#0A0A0A",
  },
  {
    id: "matomo",
    name: "Matomo",
    description: "Self-hosted, full-control analytics. Host + site id + token.",
    status: "live",
    accent: "#3450A3",
  },
];

export function getAnalyticsProviderMeta(
  id: string,
): AnalyticsProviderMeta | undefined {
  return ANALYTICS_PROVIDER_CATALOG.find((p) => p.id === id);
}

/** Chip size in px for each density. */
const SIZE_PX: Record<NonNullable<ProviderLogoProps["size"]>, number> = {
  sm: 28,
  md: 40,
  lg: 56,
};

export interface ProviderLogoProps {
  provider: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

/** Inline fallback for any slug without a real asset. */
function CustomLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden>
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" stroke="#9AA0A6" strokeWidth="1.6" strokeDasharray="3 2.5" />
      <path d="M12 8.5v7M8.5 12h7" stroke="#9AA0A6" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

/**
 * A provider's genuine brand mark on the canonical white logo chip. Mirrors the
 * Integrations marketplace `ProviderLogo` so real logos read identically across
 * the app; an unknown slug falls back to a neutral custom glyph.
 */
export function ProviderLogo({ provider, size = "md", className }: ProviderLogoProps) {
  const px = SIZE_PX[size];
  const pad = Math.round(px * 0.18);
  const padUnits = pad / 4;
  const file = BRAND_LOGO_FILES[provider];
  const meta = getAnalyticsProviderMeta(provider);
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-black/10",
        className,
      )}
      style={{ width: px, height: px, padding: `calc(var(--spacing) * ${padUnits})` }}
    >
      {file ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={`/brand-logos/${file}`}
          alt={meta ? `${meta.name} logo` : `${provider} logo`}
          className="h-full w-full object-contain"
          loading="lazy"
        />
      ) : (
        <CustomLogo className="h-full w-full" />
      )}
    </span>
  );
}
